import { NgModule } from '@angular/core';
import { RouterModule, Routes, UrlMatchResult, UrlSegment } from '@angular/router';
import { LangGuard } from './shared/guards/lang.guard';

/**
 * Ficha de tour: /<id numérico>/<slug>/<lang>.
 * Antes era ':productId/:productTitle/:lang' y aceptaba cualquier primer segmento:
 * '/algo/slug/en' llegaba a la ficha, parseInt daba NaN y el navegador redirigía
 * a '/NaN/slug/en' (URLs basura indexadas). Ahora lo que no empieza con número cae en 404.
 */
export function productMatcher(segments: UrlSegment[]): UrlMatchResult | null {
    if (segments.length === 3 && /^\d+$/.test(segments[0].path)) {
        return {
            consumed: segments,
            posParams: { productId: segments[0], productTitle: segments[1], lang: segments[2] },
        };
    }
    return null;
}

const LANG_AWARE_CONFIGS: Array<{ path: string; loadChildren: () => Promise<any> }> = [
    { path: 'home', loadChildren: () => import('./pages/home/home.module').then(m => m.HomePageModule) },
    { path: 'about', loadChildren: () => import('./pages/about/about.module').then(m => m.AboutPageModule) },
    { path: 'faqs', loadChildren: () => import('./pages/faqs/faqs.module').then(m => m.FaqsPageModule) },
    { path: 'buenos-aires-tours', loadChildren: () => import('./pages/buenos-aires-tours/buenos-aires-tours.module').then(m => m.BuenosAiresToursModule) },
    { path: 'buenos-aires-tours/sightseeing', loadChildren: () => import('./pages/sightseeing/sightseeing.module').then(m => m.SightseeingModule) },
    { path: 'buenos-aires-tours/unconventional', loadChildren: () => import('./pages/unconventional/unconventional.module').then(m => m.UnconventionalModule) },
    { path: 'buenos-aires-tours/food-drinks', loadChildren: () => import('./pages/food-drinks/food-drinks.module').then(m => m.FoodDrinksModule) },
    { path: 'buenos-aires-tours/art-culture', loadChildren: () => import('./pages/art-culture/art-culture.module').then(m => m.ArtCultureModule) },
    { path: 'buenos-aires-tours/shore-excursions', loadChildren: () => import('./pages/shore-excursions/shore-excursions.module').then(m => m.ShoreExcursionsModule) },
    { path: 'buenos-aires-tours/tango-shows', loadChildren: () => import('./pages/tango-shows/tango-shows.module').then(m => m.TangoShowsModule) },
    { path: 'buenos-aires-tours/transfers', loadChildren: () => import('./pages/transfers/transfers.module').then(m => m.TransfersModule) },
    { path: 'buenos-aires-tours/nightlife', loadChildren: () => import('./pages/nightlife/nightlife.module').then(m => m.NightlifeModule) },
    { path: 'buenos-aires-tours/history', loadChildren: () => import('./pages/history/history.module').then(m => m.HistoryModule) },
    { path: 'buenos-aires-tours/nature', loadChildren: () => import('./pages/nature/nature.module').then(m => m.NatureModule) },
    { path: 'contact', loadChildren: () => import('./pages/contact/contact.module').then(m => m.ContactPageModule) },
    { path: 'destino/uruguay', loadChildren: () => import('./pages/destination-uruguay/destination-uruguay.module').then(m => m.DestinationUruguayPageModule) },
    { path: 'destino/buenos-aires', loadChildren: () => import('./pages/destination-argentina/destination-argentina.module').then(m => m.DestinationArgentinaPageModule) },
    { path: 'destino/chile', loadChildren: () => import('./pages/destination-chile/destination-chile.module').then(m => m.DestinationChilePageModule) },
    { path: 'buenos-aires-in-1-day', loadChildren: () => import('./pages/buenos-aires-in-one-day/buenos-aires-in-one-day.module').then(m => m.BuenosAiresInOneDayPageModule) },
    { path: 'buenos-aires-in-3-days', loadChildren: () => import('./pages/buenos-aires-in-three-days/buenos-aires-in-three-days.module').then(m => m.BuenosAiresInThreeDaysPageModule) },
    { path: 'buenos-aires-in-5-days', loadChildren: () => import('./pages/buenos-aires-in-five-days/buenos-aires-in-five-days.module').then(m => m.BuenosAiresInFiveDaysPageModule) },
    { path: 'buenos-aires-blog', loadChildren: () => import('./pages/blog/blog.module').then(m => m.BlogPageModule) },
    { path: 'search', loadChildren: () => import('./pages/search/search.module').then(m => m.SearchModule) },
    { path: 'spanish-tips', loadChildren: () => import('./pages/spanish-tips/spanish-tips.module').then(m => m.SpanishTipsPageModule) },
    { path: 'neighborhoods-guide', loadChildren: () => import('./pages/neighborhoods-guide/neighborhoods-guide.module').then(m => m.NeighborhoodsGuideModule) },
    { path: 'qr-form', loadChildren: () => import('./qr-form/qr-form.module').then(m => m.QrFormModule) },
    { path: 'qr-form-ts', loadChildren: () => import('./qr-form-ts/qr-formts.module').then(m => m.QrFormtsModule) },
    { path: 'qr-form-ct', loadChildren: () => import('./qr-form-ct/qr-formtc.module').then(m => m.QrFormtcModule) },
    { path: 'qr-form-ee', loadChildren: () => import('./qr-form-ee/qr-formee.module').then(m => m.QrFormeeModule) },
    { path: 'bokun-list', loadChildren: () => import('./bokun-widget/bokun-widget.module').then(m => m.BokunWidgetModule) },
    // Concierge route disabled.
    // {path: 'concierge', loadChildren: () => import('./concierge/concierge.module').then(m => m.ConciergeModule), },
];

const LEGACY_DESTINATIONS: Array<{ from: string; to: string }> = [
    { from: 'argentina', to: 'destino/buenos-aires' },
    { from: 'uruguay', to: 'destino/uruguay' },
    { from: 'chile', to: 'destino/chile' },
];

const LEGACY_ROUTES: Routes = LEGACY_DESTINATIONS.reduce((acc, { from, to }) => {
    acc.push({ path: from, pathMatch: 'full', redirectTo: `${to}/en` });
    acc.push({ path: `${from}/:lang`, pathMatch: 'full', redirectTo: `${to}/:lang` });
    return acc;
}, [] as Routes);

const LANG_ROUTES: Routes = LANG_AWARE_CONFIGS.reduce((acc, { path, loadChildren }) => {
    acc.push({ path, pathMatch: 'full', redirectTo: `${path}/en` });
    acc.push({ path: `${path}/:lang`, canActivate: [LangGuard], loadChildren });
    return acc;
}, [] as Routes);

const routes: Routes = [
    // Manejo de tráfico extraño del widget (o enlaces legacy): /china -> /home/zh-CN
    { path: 'china', pathMatch: 'full', redirectTo: 'home/zh-CN' },
    // Concierge misspelling redirects disabled (concierge route is off).
    // { path: 'conserge', pathMatch: 'full', redirectTo: 'concierge/en' },
    // { path: 'concergie', pathMatch: 'full', redirectTo: 'concierge/en' },
    // Portada canónica en '/' (inglés). /home/en -> 301 a '/' lo hace el servidor (ssr/server.ts).
    { path: '', pathMatch: 'full', loadChildren: () => import('./pages/home/home.module').then(m => m.HomePageModule) },
    ...LEGACY_ROUTES,
    // Blog legacy
    { path: 'blog/:slug', pathMatch: 'full', redirectTo: 'buenos-aires-blog/:slug/en' },
    { path: 'blog/:slug/:lang', pathMatch: 'full', redirectTo: 'buenos-aires-blog/:slug/:lang' },
    { path: 'post', pathMatch: 'full', redirectTo: 'post/en' },
    { path: 'post/:lang', canActivate: [LangGuard], loadChildren: () => import('./pages/post-detail/post-detail.module').then(m => m.PostDetailModule) },
    ...LANG_ROUTES,
    // Post detail canónico por slug
    { path: 'buenos-aires-blog/:slug/:lang', canActivate: [LangGuard], loadChildren: () => import('./pages/post-detail/post-detail.module').then(m => m.PostDetailModule) },
    // Producto canónico con idioma al final
    { matcher: productMatcher, canActivate: [LangGuard], loadChildren: () => import('./pages/product/product.module').then(m => m.ProductPageModule) },
    // Admin (no language guard, no SEO indexing).
    { path: 'admin', pathMatch: 'full', redirectTo: 'admin/prices' },
    { path: 'admin/prices', loadChildren: () => import('./pages/admin-prices/admin-prices.module').then(m => m.AdminPricesPageModule) },
    // Fallback global: renderiza 404 real (sin redirigir al home)
    { path: '**', loadChildren: () => import('./pages/not-found/not-found.module').then(m => m.NotFoundModule) },
];

@NgModule({
    imports: [
        RouterModule.forRoot(routes, {
            initialNavigation: 'enabledBlocking',
            scrollPositionRestoration: 'top',
        }),
    ],
    exports: [RouterModule],
})
export class AppRoutingModule { }
